import { useState } from 'react';

export interface SegmentoComposicao {
  id: string;
  label: string;
  valor: number;
  valorLabel: string;
  color: string;
}

export interface LinhaComposicao {
  id: string;
  nome: string;
  totalLabel: string;
  total: number;
  segmentos: SegmentoComposicao[];
}

/**
 * Part-to-whole per person: one stacked bar per eletricista, each segment an
 * obra. Bars share a scale so lengths compare across people; a 2px surface gap
 * separates segments so adjacent hues never touch, and the hovered segment is
 * named in words below the bar.
 */
export function ComposicaoEmpilhada({ linhas }: { linhas: LinhaComposicao[] }) {
  const [hover, setHover] = useState<{ linha: string; segmento: SegmentoComposicao } | null>(null);
  const max = Math.max(1, ...linhas.map((l) => l.total));

  return (
    <ul className="flex flex-col gap-3.5">
      {linhas.map((linha) => (
        <li key={linha.id}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[12px] font-medium text-conduril-ink">
              {linha.nome}
            </span>
            <span className="num shrink-0 text-[12px] font-semibold text-conduril">
              {linha.totalLabel}
            </span>
          </div>

          <div
            className="mt-1.5 flex h-3 gap-[2px] overflow-hidden rounded-full bg-conduril-tint"
            style={{ width: `${Math.max(6, (linha.total / max) * 100)}%` }}
          >
            {linha.segmentos.map((seg) => (
              <button
                key={seg.id}
                type="button"
                aria-label={`${linha.nome} · ${seg.label}: ${seg.valorLabel}`}
                onMouseEnter={() => setHover({ linha: linha.id, segmento: seg })}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover({ linha: linha.id, segmento: seg })}
                onBlur={() => setHover(null)}
                className="h-full first:rounded-l-full last:rounded-r-full focus:outline-none
                           focus-visible:ring-2 focus-visible:ring-conduril/40"
                style={{
                  width: `${(seg.valor / linha.total) * 100}%`,
                  background: seg.color,
                }}
              />
            ))}
          </div>

          <div className="mt-1 h-4 text-[10px] leading-4">
            {hover?.linha === linha.id ? (
              <span className="text-[#5c6373]">
                {hover.segmento.label} ·{' '}
                <span className="num font-semibold">{hover.segmento.valorLabel}</span>
              </span>
            ) : (
              <span className="text-conduril-muted">
                {linha.segmentos.length > 0 ? linha.segmentos[0].label : ''}
                {linha.segmentos.length > 1 ? ` +${linha.segmentos.length - 1}` : ''}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
